<template>
  <BaseDialog
    :open="open"
    :max-width="'1200px'"
    @close="onClose"
  >
    <template #title>
      Schüler zuweisen
    </template>

    <div class="form">
      <div class="field">
        <label for="flt-students">Verfügbare Schüler</label>
        <input
          id="flt-students"
          v-model="studentFilter"
          class="input"
          type="text"
          autocomplete="off"
          placeholder="Filtern (Name/Nummer)"
          :disabled="loading"
        >

        <div class="chip-box">
          <div class="chips">
            <button
              v-for="s in filteredPool"
              :key="s.id"
              type="button"
              class="chip"
              :disabled="loading"
              :title="studentLabel(s)"
              @click="addStudent(s.id)"
            >
              {{ studentLabel(s) }}
            </button>
            <p
              v-if="filteredPool.length===0"
              class="muted"
            >
              Keine Treffer.
            </p>
          </div>
        </div>

        <!-- Gruppen (Sammelzuweisung) -->
        <div
          class="field"
          style="margin-top:12px;"
        >
          <label for="flt-groups">Gruppen (Sammelzuweisung)</label>
          <input
            id="flt-groups"
            v-model="groupFilter"
            class="input"
            type="text"
            autocomplete="off"
            placeholder="Gruppen filtern"
            :disabled="loadingGroups"
          >
          <div
            class="chip-box"
            style="margin-top:8px;"
          >
            <div class="chips">
              <button
                v-for="g in filteredGroups"
                :key="g.id"
                type="button"
                :class="['chip', g.is_private ? 'warn' : '']"
                :disabled="loadingGroups"
                :title="groupLabel(g)"
                @click="addGroup(g.id)"
              >
                {{ groupLabel(g) }}
              </button>
              <p
                v-if="loadingGroups"
                class="muted"
              >
                Gruppen werden geladen…
              </p>
              <p
                v-else-if="filteredGroups.length===0"
                class="muted"
              >
                Keine Gruppen verfügbar.
              </p>
            </div>
          </div>
          <p
            v-if="visibleGroups.length"
            class="help"
          >
            Tipp: Klick auf eine Gruppe wählt alle Mitglieder aus.
          </p>
        </div>
      </div>

      <div class="field">
        <label>Zugewiesene Schüler</label>
        <div class="chip-box">
          <div class="chips">
            <button
              v-for="s in selectedStudents"
              :key="s.id"
              type="button"
              class="chip active"
              :disabled="loading"
              :title="studentLabel(s)"
              @click="removeStudent(s.id)"
            >
              {{ studentLabel(s) }}
            </button>
            <p
              v-if="selectedIds.size===0"
              class="muted"
            >
              Noch keine Schüler ausgewählt.
            </p>
          </div>
        </div>

        <p class="help">
          <strong>Hinweis:</strong> Durch das Entfernen von Schülern, verlieren diese ihre Vor- und Rückblätter in
          diesem Lernbrief.
        </p>
      </div>

      <p
        v-if="error"
        class="notice notice-danger mt-12"
        role="alert"
      >
        {{ error }}
      </p>
    </div>

    <template #footer>
      <div style="display:flex;gap:8px;justify-content:flex-end;">
        <button
          class="btn"
          type="button"
          :disabled="loading"
          @click="onClose"
        >
          Abbrechen
        </button>
        <button
          class="btn btn-primary"
          type="button"
          :disabled="loading"
          @click="onSave"
        >
          {{ loading ? 'Speichern…' : 'Speichern' }}
        </button>
      </div>
    </template>
  </BaseDialog>
</template>

<script setup>
import {computed, onMounted, ref, watch} from 'vue';
import BaseDialog from '@/components/BaseDialog.vue';
import {fetchActiveStudents} from '@/api/students';
import {getLetterStudents, setLetterStudents} from '@/api/feedbackLetters';
import api from '@/services/api';
import {useToast} from 'primevue/usetoast';

const props = defineProps({
  open: {type: Boolean, default: false},
  letterId: {type: Number, required: true},
});
const emit = defineEmits(['close', 'saved']);
const toast = useToast();

const loading = ref(false);
const error = ref('');
const allStudents = ref([]);
const assigned = ref([]); // vom Server

const studentFilter = ref('');
const selectedIds = ref(new Set());

// Gruppen-State
const loadingGroups = ref(false);
const groupFilter = ref('');
const groups = ref([]);                 // alle Gruppen
const groupMembersMap = ref(new Map()); // Map<groupId, Set<studentId>>

const poolStudents = computed(() => {
  const ids = selectedIds.value;
  return (allStudents.value || []).filter(s => !ids.has(Number(s.id)));
});
const selectedStudents = computed(() => {
  const ids = selectedIds.value;
  return (allStudents.value || []).filter(s => ids.has(Number(s.id)));
});
const filteredPool = computed(() => {
  const q = (studentFilter.value || '').trim().toLowerCase();
  if (!q) return poolStudents.value;
  return poolStudents.value.filter(s => studentLabel(s).toLowerCase().includes(q));
});

const visibleGroups = computed(() => {
  return groups.value.filter(g => {
    const members = groupMembersMap.value.get(Number(g.id)) || new Set();
    if (members.size === 0) return false;
    // Zeige Gruppe nur, wenn sie noch mindestens ein nicht ausgewähltes Mitglied hat
    for (const sid of members) {
      if (!selectedIds.value.has(Number(sid))) return true;
    }
    return false;
  });
});
const filteredGroups = computed(() => {
  const q = (groupFilter.value || '').trim().toLowerCase();
  if (!q) return visibleGroups.value;
  return visibleGroups.value.filter(g => groupLabel(g).toLowerCase().includes(q));
});

function studentLabel(s) {
  const num = s.student_number ? ` · ${s.student_number}` : '';
  return `${s.last_name}, ${s.first_name}${num}`;
}

function groupLabel(g) {
  const count = (groupMembersMap.value.get(Number(g.id)) || new Set()).size;
  const base = `${g.name}${count ? ' (' + count + ')' : ''}`;
  const priv = g.is_private ? ' - privat' : '';
  return `${base}${priv}`;
}

function addGroup(groupId) {
  const gid = Number(groupId);
  const members = groupMembersMap.value.get(gid) || new Set();
  if (members.size === 0) return;
  const s = new Set(selectedIds.value);
  members.forEach(id => s.add(Number(id)));
  selectedIds.value = s;
}

function addStudent(id) {
  const set = new Set(selectedIds.value);
  set.add(Number(id));
  selectedIds.value = set;
}

function removeStudent(id) {
  const set = new Set(selectedIds.value);
  set.delete(Number(id));
  selectedIds.value = set;
}

async function loadGroupsInBackground() {
  loadingGroups.value = true;
  try {
    const {data: gr} = await api.get('/groups', {params: {page: 1, limit: 500, withCounts: false}});
    groups.value = Array.isArray(gr?.items) ? gr.items : [];
    const memberPairs = await Promise.all(
        groups.value.map(async (g) => {
          try {
            const {data} = await api.get(`/groups/${g.id}/members`);
            const ids = new Set((Array.isArray(data?.items) ? data.items : []).map(s => Number(s.id)));
            return [Number(g.id), ids];
          } catch {
            return [Number(g.id), new Set()];
          }
        })
    );
    groupMembersMap.value = new Map(memberPairs);
  } catch {
    groups.value = [];
    groupMembersMap.value = new Map();
  } finally {
    loadingGroups.value = false;
  }
}

async function loadData() {
  loading.value = true;
  error.value = '';
  try {
    const [st, cur] = await Promise.all([
      fetchActiveStudents(),
      getLetterStudents(props.letterId)
    ]);
    allStudents.value = Array.isArray(st) ? st : [];
    assigned.value = Array.isArray(cur?.items) ? cur.items : [];
    selectedIds.value = new Set((assigned.value || []).map(s => Number(s.id)));
  } catch (e) {
    error.value = e?.response?.data?.error?.message || e?.response?.data?.message || 'Daten konnten nicht geladen werden.';
  } finally {
    loading.value = false;
  }
}

async function onSave() {
  loading.value = true;
  error.value = '';
  try {
    const ids = Array.from(selectedIds.value);
    await setLetterStudents(props.letterId, ids);

    toast.add({severity: 'success', summary: 'Schülerzuordnung gespeichert.', life: 3500})
    emit('saved', {letterId: props.letterId, count: ids.length});
  } catch (e) {
    error.value = e?.response?.data?.error?.message ||
        e?.response?.data?.message ||
        'Speichern fehlgeschlagen.';
    toast.add({severity: 'error', summary: error.value, life: 3500})
  } finally {
    loading.value = false;
  }
}

function onClose() {
  emit('close');
}

watch(() => props.open, async (val) => {
  if (val && props.letterId) {
    studentFilter.value = '';
    groupFilter.value = '';
    selectedIds.value = new Set();
    await Promise.all([loadData(), loadGroupsInBackground()]);
  } else {
    error.value = '';
    allStudents.value = [];
    assigned.value = [];
    groups.value = [];
    groupMembersMap.value = new Map();
  }
});

onMounted(() => {
  // noop
});
</script>

<style scoped>
.form {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.input {
  height: 36px;
  padding: 8px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--surface);
}

.chip-box {
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  padding: 8px;
  margin-top: 8px;
  min-height: 80px;
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-height: 260px;
  overflow: auto;
}

.chip {
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 13px;
  line-height: 1.4;
}

.chip.active {
  background: rgba(79, 70, 229, 0.12);
  border-color: var(--primary);
  color: var(--primary-600);
}

.chip.warn {
  background: rgba(245, 158, 11, 0.12);
  border-color: rgba(245, 158, 11, 0.5);
  color: var(--warn);
}

.muted {
  color: var(--muted);
}

.notice {
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 8px 10px;
}

.help {
  font-size: 12px;
  line-height: 16px;
  color: var(--muted);
}
</style>
